import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { gamificationService } from '../../../lib/gamificationService';

const ActiveChallenges = ({ challenges, userId, onRefresh }) => {
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [joiningChallenge, setJoiningChallenge] = useState(null);

  const handleJoinChallenge = async (challengeId) => {
    setJoiningChallenge(challengeId);
    try {
      const { error } = await gamificationService?.joinChallenge(challengeId, userId);
      if (!error) {
        alert('Successfully joined challenge!');
        onRefresh?.();
      } else {
        alert('Failed to join challenge. You may already be participating.');
      }
    } catch (error) {
      console.error('Error joining challenge:', error);
      alert('An error occurred while joining the challenge.');
    } finally {
      setJoiningChallenge(null);
    }
  };

  const getChallengeTypeIcon = (type) => {
    switch (type) {
      case 'individual':
        return 'User';
      case 'team':
        return 'Users';
      case 'gym_wide':
        return 'Building';
      default:
        return 'Target';
    }
  };

  const getChallengeTypeColor = (type) => {
    switch (type) {
      case 'individual':
        return 'bg-blue-100 text-blue-800';
      case 'team':
        return 'bg-green-100 text-green-800';
      case 'gym_wide':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (!challenges || challenges?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <Icon name="Target" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Challenges</h3>
        <p className="text-gray-600">Check back soon for new challenges or create your own!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {challenges?.map((challenge) => {
          const daysRemaining = getDaysRemaining(challenge?.end_date);
          const participationRate = challenge?.max_participants
            ? (challenge?.current_participants / challenge?.max_participants) * 100
            : 0;

          return (
            <div
              key={challenge?.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedChallenge(challenge)}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getChallengeTypeColor(challenge?.challenge_type)}`}>
                        <Icon name={getChallengeTypeIcon(challenge?.challenge_type)} className="w-3 h-3 inline mr-1" />
                        {challenge?.challenge_type?.replace('_', ' ')?.toUpperCase()}
                      </span>
                      {daysRemaining <= 7 && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <Icon name="Clock" className="w-3 h-3 inline mr-1" />
                          {daysRemaining} days left
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{challenge?.title}</h3>
                    <p className="text-gray-600 mt-1 line-clamp-2">{challenge?.description}</p>
                  </div>
                </div>

                {/* Challenge Details */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Goal:</span>
                    <span className="font-semibold text-gray-900">
                      {challenge?.goal_value} {challenge?.goal_type?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Reward:</span>
                    <span className="font-semibold text-orange-600">
                      {challenge?.points_reward} points
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-semibold text-gray-900">
                      {formatDate(challenge?.start_date)} - {formatDate(challenge?.end_date)}
                    </span>
                  </div>
                  {challenge?.prize_description && (
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Gift" className="w-4 h-4 text-purple-600" />
                      <span className="text-purple-600 font-medium">{challenge?.prize_description}</span>
                    </div>
                  )}
                </div>

                {/* Participation Progress */}
                {challenge?.max_participants && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Participants</span>
                      <span className="font-semibold text-gray-900">
                        {challenge?.current_participants} / {challenge?.max_participants}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-orange-600 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(participationRate, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <Button
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleJoinChallenge(challenge?.id);
                  }}
                  disabled={joiningChallenge === challenge?.id}
                  className="w-full"
                >
                  {joiningChallenge === challenge?.id ? (
                    <>
                      <Icon name="Loader" className="w-4 h-4 animate-spin mr-2" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <Icon name="UserPlus" className="w-4 h-4 mr-2" />
                      Join Challenge
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {/* Challenge Details Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{selectedChallenge?.title}</h2>
                <button
                  onClick={() => setSelectedChallenge(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600">{selectedChallenge?.description}</p>
                </div>

                {selectedChallenge?.rules && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Rules</h3>
                    <p className="text-gray-600">{selectedChallenge?.rules}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Challenge Type</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getChallengeTypeColor(selectedChallenge?.challenge_type)}`}>
                      {selectedChallenge?.challenge_type?.replace('_', ' ')?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Points Reward</h3>
                    <p className="text-orange-600 font-bold text-lg">{selectedChallenge?.points_reward} points</p>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    handleJoinChallenge(selectedChallenge?.id);
                    setSelectedChallenge(null);
                  }}
                  disabled={joiningChallenge === selectedChallenge?.id}
                  className="w-full"
                >
                  <Icon name="UserPlus" className="w-4 h-4 mr-2" />
                  Join This Challenge
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveChallenges;